import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UserService } from '../../services/user.service';
import { ModalController } from '@ionic/angular';
import { ViewCertificateModalComponent } from '../view-certificate-modal/view-certificate-modal.component';
import { JobService } from '../../services/job.service';
import { TalentJobFormatService } from 'src/modules/talent-dashboard/services/talent-job-format.service';
import { ShiftHistoryDetailComponent } from '../shift-history-detail/shift-history-detail.component';
import { ImagesService } from '../../services/images.service';
import { IContractorHistory } from 'src/modules/company-dashboard/interfaces/contractorHistory.interface';
import { IContractorShiftStats } from 'src/modules/company-dashboard/interfaces/IContractorShiftStats.interface';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { IClientJob } from 'src/modules/company-dashboard/interfaces/client-job.interface';
import { PopupService } from '../../services/popup.service';
import { IBulletin } from '../../interfaces/bulletin.interface';
import * as moment from 'moment';
import { BulletinViewerComponent } from '../bulletin-viewer/bulletin-viewer.component';
import { DownloadService } from '../../services/download.service';
import { getApplicantQualifyingOtherSkill, skillShortLabel } from 'src/modules/shared/utils/helpers/psw-pca-pair.helper';

@Component({
  selector: 'app-shift-detail-container',
  templateUrl: './shift-detail-container.component.html',
  styleUrls: ['./shift-detail-container.component.scss'],
})
export class ShiftDetailContainerComponent implements OnInit {

  @Input() staffer;
  @Input() userData: IUser;
  @Input() viewingHistory: boolean;
  @Input() jobItem : IClientJob;
  // Shift role, passed on screens (e.g. applied applicants) that must NOT pass the full
  // jobItem - passing jobItem there would wrongly render the hired-only attendance panel.
  @Input() shiftSkill: string;
  // acceptableSkills companion to shiftSkill, for the same screens that can't pass jobItem.
  @Input() shiftAcceptableSkills: string[];
  @Input() viewingHired: boolean = false
  // Opt-in reskin hook: the client requests page passes 'staffy-requests'; every other caller renders as before
  @Input() cssClass: string = '';

  emailCopied : boolean = false;
  showEmailPopOver : boolean = false;
  phoneNumberCopied : boolean = false;
  showPhoneNumberPopOver : boolean = false;
  hidePopoverTimeoutId;
  isFilled: boolean = false;
  shouldShowPhoneNumber: boolean = false;
  @Output() hireContractor = new EventEmitter();
  showContractorDetails: boolean = true;
  showBulletinDetails : boolean = true;
  showHistoryDetails : boolean = false;


  shiftHistoryStats: IContractorShiftStats;
  showHistory: boolean = false;
  bulletins : Array<IBulletin> =  [];
  constructor(
    private userService: UserService,
    private modalController: ModalController,
    private jobService: JobService,
    private talentJobFormatService: TalentJobFormatService,
    private imageService: ImagesService,
    private downloadService: DownloadService,
    private popupService: PopupService

  ) { }

  ngOnInit() {
    this.updateAcknowledgedBulletins()
    this.shouldShowPhoneNumber = this.canViewContractorPhoneNumber()
  }

  // "Qualified via PCA" style badge label — null unless this applicant holds
  // the shift's acceptable-but-not-primary skill instead of its primary one.
  get qualifyingOtherSkillLabel(): string | null {
    const stafferUser = this.staffer?.staffer || this.staffer;
    const job = {
      skill: this.shiftSkill || this.jobItem?.skill,
      acceptableSkills: this.shiftAcceptableSkills || (this.jobItem as any)?.acceptableSkills,
    };
    const otherSkill = getApplicantQualifyingOtherSkill(stafferUser, job);
    return otherSkill ? skillShortLabel(otherSkill) : null;
  }

  

  async viewStafferCertificates(id: string, isCOVID: boolean) {
    const approvedCertificates = await this.userService.getApprovedCertificatesOfStaffer(id, isCOVID);

    // Add user's resume to the approved certificates list if it exists and company is SE Health
    // Check if resumeUrl exists either on staffer.staffer or directly on staffer
    if (this.shouldShowResumeButton()) {
      const resumeUrl = this.staffer?.staffer?.resumeUrl || this.staffer?.resumeUrl;
      if (resumeUrl?.secure_url) {
        approvedCertificates.push({
          url: resumeUrl.secure_url,
          type: 'Resume',
          _id: null,
          isApprove: true,
          uploadedDate: null,
          isCertificatePDF: false,
          isCertificateDOC: false,
          isCertificateImage: false,
          sanitizeURL: null
        });
      }
    }

    const myModal = await this.modalController.create({
      component: ViewCertificateModalComponent,
      // Reskinned client requests page: the viewer joins the staffy-modal kit
      cssClass: this.cssClass ? 'viewCertificateModal staffy-modal' : 'viewCertificateModal',
      componentProps: {
        approvedCertificates,
        isCOVID: isCOVID,
        cssClass: this.cssClass
      }
    });
    await myModal.present();
  }

  // Clean, employer-facing labels per apparel key (no "(Required)"/"(Optional)"
  // suffixes). Mirrors server/api/industry/apparel.json / the apparel-requirements page.
  private readonly APPAREL_LABELS: { [key: string]: string } = {
    black_shirt: 'Black Dress Shirt',
    white_shirt: 'White Dress Shirt',
    black_pants: 'Black Pants',
    black_shoes: 'Black Dress Shoes',
    black_socks: 'Black Socks',
    foh_kit: 'FOH Kit',
    black_belt: 'Black Belt',
    black_vest: 'Black Vest',
    black_tie: 'Black Tie',
    non_slip_shoes: 'Non Slip Shoes',
    kitchen_black_white: 'Kitchen Blacks/Whites',
    white_chef_jacket: 'White Chef Jacket',
    black_chef_jacket: 'Black Chef Jacket',
    knife_kit: 'Knife Kit',
    safety_shoes: 'Safety Shoes'
  };

  /**
   * View a contractor's uploaded apparel / equipment pictures, mirroring the
   * "View Credentials" flow. Only the apparel the client demanded for this shift
   * is shown; rejected (failed) pictures are filtered out and status is hidden.
   */
  async viewStafferApparels(id?: string) {
    let staffer = this.staffer && this.staffer.staffer;

    // The applicant payload is projected without apparel data, so fetch just the
    // apparel pictures on demand (lightweight, projection-only endpoint).
    const hasApparelData = staffer &&
      (staffer.uniformPictureFOH || staffer.uniformPictureBOH || staffer.uniformPictureGeneral);
    if (!hasApparelData && id) {
      try {
        staffer = await this.userService.getStafferApparels(id);
      } catch (error) {
        console.error('Error fetching apparel data: ', error);
      }
    }

    const demandedKeys = await this.getDemandedApparelKeys();
    const apparels = this.getUploadedApparels(staffer, demandedKeys);

    if (!apparels.length) {
      await this.popupService.showModal({
        heading: 'Apparel / Equipment',
        message: demandedKeys && demandedKeys.length
          ? 'This contractor has not uploaded the apparel required for this shift.'
          : 'This contractor has not uploaded any apparel or equipment pictures.',
        btn: 'Dismiss',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png'
      });
      return;
    }

    const myModal = await this.modalController.create({
      component: ViewCertificateModalComponent,
      cssClass: this.cssClass ? 'viewCertificateModal staffy-modal' : 'viewCertificateModal',
      componentProps: {
        cssClass: this.cssClass,
        approvedCertificates: apparels,
        isCOVID: false
      }
    });
    await myModal.present();
  }

  /**
   * The apparel keys the client demanded for this shift's role, or null when the
   * client hasn't configured apparel requirements (fall back to showing all uploads).
   *
   * The entity snapshot on userData can be stale (the client may have edited apparel
   * after login), so prefer the live config and fall back to the snapshot on any
   * fetch failure - mirrors talent-job-checks resolveApparelRequirement().
   */
  private async getDemandedApparelKeys(): Promise<string[] | null> {
    const entity: any = this.userData && this.userData.entityId;
    if (!entity) {
      return null;
    }

    let config: any = entity.apparelRequirements;
    if (entity._id) {
      try {
        const fresh: any = await this.jobService.getEntityApparelRequirements(entity._id);
        if (fresh) {
          config = fresh;
        }
      } catch (e) {
        console.log('apparel: fresh apparel-requirements fetch failed, using snapshot', e);
      }
    }

    if (!config || !config.enabled || !Array.isArray(config.requirements)) {
      return null;
    }
    const skill = this.getShiftSkill().toLowerCase();
    if (!skill) {
      return null;
    }
    const row = config.requirements.find((r: any) => (r.skill || '').toLowerCase() === skill);
    if (!row || !Array.isArray(row.requiredApparel) || !row.requiredApparel.length) {
      return null;
    }
    return row.requiredApparel;
  }

  private getShiftSkill(): string {
    const job: any = this.jobItem;
    const jobSkill = job && (job.skill || (job.jobs && job.jobs[0] && job.jobs[0].skill));
    return jobSkill || this.shiftSkill || '';
  }

  // Apparel/uniform is a hospitality concept; healthcare (and retirement home) clients
  // don't use it, so the "View Apparel" option is hidden for them.
  get isHealthcareClient(): boolean {
    const healthcareTypes = ['Healthcare', 'Retirement Home'];
    const entity: any = this.userData && this.userData.entityId;
    const userType = (this.userData && this.userData.companyType) || '';
    const entityType = (entity && entity.companyType) || '';
    return healthcareTypes.indexOf(userType) !== -1 || healthcareTypes.indexOf(entityType) !== -1;
  }

  /**
   * Flatten a staffer's FOH/BOH/General uniform groups into viewable items,
   * keeping only uploaded, non-rejected pictures. When demandedKeys is provided,
   * only those apparel items (demanded by the client for this shift) are included.
   */
  private getUploadedApparels(staffer, demandedKeys: string[] | null): any[] {
    if (!staffer) {
      return [];
    }

    const groups = [
      staffer.uniformPictureFOH,
      staffer.uniformPictureBOH,
      staffer.uniformPictureGeneral
    ];
    const demandSet = demandedKeys && demandedKeys.length ? new Set(demandedKeys) : null;

    const apparels = [];
    groups.forEach(group => {
      if (!group) {
        return;
      }
      Object.keys(group).forEach(key => {
        // When the client has demanded specific apparel, show only those.
        if (demandSet && !demandSet.has(key)) {
          return;
        }
        const apparel = group[key];
        const secureUrl = apparel && apparel.url && (apparel.url.secure_url || apparel.url.url);

        // Skip not-yet-uploaded and rejected apparel.
        if (!secureUrl || apparel.status === 'fail') {
          return;
        }

        apparels.push({
          url: secureUrl,
          originalCloudinaryUrl: apparel.url && apparel.url.url,
          type: this.apparelLabel(key, apparel.name),
          _id: null,
          isApprove: true,
          uploadedDate: null,
          isCertificatePDF: false,
          isCertificateDOC: false,
          isCertificateImage: false,
          sanitizeURL: null
        });
      });
    });

    return apparels;
  }

  private apparelLabel(key: string, name?: string): string {
    if (this.APPAREL_LABELS[key]) {
      return this.APPAREL_LABELS[key];
    }
    const cleaned = this.cleanApparelName(name);
    return cleaned || this.prettifyApparelKey(key);
  }

  // Strip the "(Required)" / "(Optional)" suffixes (and any trailing comma) from a stored name.
  private cleanApparelName(name?: string): string {
    if (!name || name === 'undefined') {
      return '';
    }
    return name
      .replace(/\((required|optional)\)/gi, '')
      .replace(/,\s*$/, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private prettifyApparelKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  async viewStafferResume() {
    // Check if resumeUrl exists either on staffer.staffer or directly on staffer
    const resumeUrl = this.staffer?.staffer?.resumeUrl || this.staffer?.resumeUrl;

    if (!resumeUrl?.secure_url) {
      return;
    }

    const resumeCertificate = [{
      url: resumeUrl.secure_url,
      type: 'Resume',
      _id: null,
      isApprove: true,
      uploadedDate: null,
      isCertificatePDF: false,
      isCertificateDOC: false,
      isCertificateImage: false,
      sanitizeURL: null
    }];

    const myModal = await this.modalController.create({
      component: ViewCertificateModalComponent,
      cssClass: this.cssClass ? 'viewCertificateModal staffy-modal' : 'viewCertificateModal',
      componentProps: {
        cssClass: this.cssClass,
        approvedCertificates: resumeCertificate,
        isCOVID: false
      }
    });
    await myModal.present();
  }

  async viewVscDeclaration() {
    const vscDeclarationURL = this.staffer?.staffer?.vscDeclarationSignedURL || this.staffer?.vscDeclarationSignedURL
      || this.staffer?.staffer?.vscDeclarationURL || this.staffer?.vscDeclarationURL;

    if (!vscDeclarationURL) {
      this.popupService.showModal({
        heading: 'VSC Declaration Unavailable',
        message: 'This contractor doesn’t have a VSC declaration on file yet.',
        btn: 'Dismiss',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png',
        specificClass: this.cssClass ? 'wideModal autoHeightModal staffy-modal staffy-requests-popup' : undefined
      });
      return;
    }

    const vscDeclaration = [{
      url: vscDeclarationURL,
      type: 'VSC Declaration',
      _id: null,
      isApprove: true,
      uploadedDate: null,
      isCertificatePDF: false,
      isCertificateDOC: false,
      isCertificateImage: false,
      sanitizeURL: null
    }];

    const myModal = await this.modalController.create({
      component: ViewCertificateModalComponent,
      cssClass: this.cssClass ? 'viewCertificateModal staffy-modal' : 'viewCertificateModal',
      componentProps: {
        cssClass: this.cssClass,
        approvedCertificates: vscDeclaration,
        isCOVID: false
      }
    });
    await myModal.present();
  }

  hasResume(): boolean {
    const resumeUrl = this.staffer?.staffer?.resumeUrl || this.staffer?.resumeUrl;
    return !!resumeUrl?.secure_url;
  }


  isDownloading: boolean = false;

  async downloadStafferCertificates(id: string) {
    try {
      this.isDownloading = true;
      const approvedCertificates = await this.userService.getApprovedCertificatesOfStaffer(id, false);

      // Add user's resume to the approved certificates list if it exists and company is SE Health
      // Check if resumeUrl exists either on staffer.staffer or directly on staffer
      if (this.shouldShowResumeButton()) {
        const resumeUrl = this.staffer?.staffer?.resumeUrl || this.staffer?.resumeUrl;
        if (resumeUrl?.secure_url) {
          approvedCertificates.push({
            url: resumeUrl.secure_url,
            type: 'Resume',
            _id: null,
            isApprove: true,
            uploadedDate: null,
            isCertificatePDF: false,
            isCertificateDOC: false,
            isCertificateImage: false,
            sanitizeURL: null
          });
        }
      }

      await this.downloadService.downloadCertificatesAsZip(id, approvedCertificates);
    } catch(error) {
      console.error('Error downloading certificates: ', error);
    } finally {
      this.isDownloading = false;
    }
  }

  async loadContractorHistory(stafferId: string) {
    if (!this.showHistoryDetails) {
      //Fetch contractor stats from server when clicked, toggle the component based on the status
      this.shiftHistoryStats = await this.jobService.fetchContractorHistoryWithClient(stafferId);
    }
    this.showHistoryDetails = !this.showHistoryDetails;
  }
  
  getProfilePicture(profileUrl) {
    return this.imageService.getProfilePic(profileUrl)
  }

  async viewShiftDetails(showPastShifts: boolean) {
    //Fetch complete history of contractor in a popup when see details is clicked, It will expand the stats data into detailed information
    //Which includes skill, rate, shift date, timings
    const shiftStatus = showPastShifts ? 'Completed by' : 'Upcoming'
	  const contractorPastShifts = await this.jobService.getShiftHistoryOfContractor(this.staffer.staffer._id, showPastShifts);
    await this.getShiftHistory(contractorPastShifts, showPastShifts, this.staffer, shiftStatus);
  }

  async getShiftHistory(contractorPastShifts: Array<IContractorHistory>, history: boolean, staffer, shiftStatus: string) {
    
    //Popup modal when see details is clicked from histroy section
    const myModal = await this.modalController.create({
      component: ShiftHistoryDetailComponent,
      backdropDismiss: false,
      cssClass: 'clientDetailModal',
      componentProps: {
        userData: this.userData,
        contractorPastShifts,
        history,
        staffer,
        shiftStatus
      }
    }); 
    
    await myModal.present();
    return await myModal.onDidDismiss()
      .then(async(dataReturned) => {
        if(dataReturned.data) {
          this.hireStaffer()
        }
        
        if(dataReturned.data === 'hireStaffer'){
           this.hireStaffer()
        }
      });      
  }


  hireStaffer() {
    this.hireContractor.emit();
  }

  async viewUnitNumbers() {
	//Show unit number popup when clicked view details
  
    if(!this.shiftHistoryStats.unitsWorked || this.shiftHistoryStats.unitsWorked.length === 0) {
        return;
    }

    this.talentJobFormatService.viewRecurringProperties(null, 'Unit Numbers', true, this.shiftHistoryStats.unitsWorked);
  }

  handlePhoneNumberCopyToClipBoard(){
    this.phoneNumberCopied=true;
  }
  
  handleEmailCopyToClipBoard(){
    this.emailCopied=true;
  }

  showPopover(popover) {
    this.emailCopied = false;
    this.phoneNumberCopied = false;
    if (this.hidePopoverTimeoutId) {
      clearTimeout(this.hidePopoverTimeoutId);
    }
  
    if (popover === 'email') {
      this.showEmailPopOver = true;
      this.showPhoneNumberPopOver = false;
    } else if (popover === 'phone') {
      this.showEmailPopOver = false;
      this.showPhoneNumberPopOver = true;
    }
  }
  
  hidePopover() {
    this.hidePopoverTimeoutId = setTimeout(() => {
      this.showEmailPopOver = false;
      this.showPhoneNumberPopOver = false;
      this.emailCopied = false;
      this.phoneNumberCopied = false;
    }, 2000);
   
  }


  /**
   * Fetch bulletins that require acknowledge and if staffer acknowledge then show date of acknowledge
   * @returns 
   */
  updateAcknowledgedBulletins(){

    if(!this.staffer.staffer || !this.staffer.staffer.bulletinsAcknowledgment) return 


    // bulletins that user already acknowledged
    const userBulletinIds = this.staffer.staffer.bulletinsAcknowledgment.map(bulletin => bulletin.bulletin_id);
    
  
    for (const bulletin of this.userData.entityId.bulletins) {
      // if bulletin does not require acknowledgment, skip it
      if (!bulletin.requires_bulletin_acknowledgment) continue;
      
      // if user already acknowledged this bulletin, set its acknowledge date for UI
      if (userBulletinIds.includes(bulletin._id)) {
        const userBulletin = this.staffer.staffer.bulletinsAcknowledgment.find(obj => bulletin._id == obj.bulletin_id);
        bulletin.acknowledgment_date = moment(userBulletin.acknowledgment_date,"X").format("DD/MM/YY")
      } 

      this.bulletins.push(bulletin)
    }
  
}


  /**
   * Show bulletin popup
   * @param bulletin 
   * @returns 
   */
  async showBulletin(bulletin : IBulletin) {
    const myModal = await this.modalController.create({
      component: BulletinViewerComponent,
      backdropDismiss: false,
      cssClass: 'wideModal',
      componentProps: {
        url: bulletin.type === 'video-url'? bulletin.url : bulletin.secure_url,
        title: bulletin.name,
        isVideo : bulletin.type === 'video-url' ? true : false,
        isAcknowledgmentRequired : false,
        forceFullWatch : !!bulletin.force_full_watch,
        bulletinId : bulletin._id,
        entityId : this.userData.entityId._id,
      }
    });
    return await myModal.present();
  
}

/**
 * Only show phone numbers of hired contractors and SE Health only
 * @returns 
 */

canViewContractorPhoneNumber(){
  let entityName = ""
  if(this.userData && this.userData.entityId && this.userData.entityId.name){
    entityName = this.userData.entityId.name
  } 

  return entityName.toUpperCase().includes("SE HEALTH") || entityName.toUpperCase().includes("CARE COMPANY")
}

shouldShowResumeButton(): boolean {
  let entityName = "";
  if (this.userData && this.userData.entityId && this.userData.entityId.name) {
    entityName = this.userData.entityId.name;
  }
  return entityName.toLowerCase().startsWith("se health");
}

}
